import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { API_URL } from '../config';
import { getValidImageUrl } from '../utils/getImageUrl';

const Checkout = () => {
  const { user, token } = useContext(AuthContext);
  const { cartItems } = useContext(CartContext);
  const navigate = useNavigate();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  
  // Billing info state
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    billingAddress: '',
    country: 'United States'
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: user.fullName || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  // If cart is empty, redirect back to cart
  if (cartItems.length === 0) {
    return <Navigate to="/cart" replace />;
  }

  const originalTotal = () => {
    return cartItems.reduce((acc, item) => acc + parseFloat(item.oldPrice || item.newPrice), 0).toFixed(2);
  };

  const calculateTotal = () => {
    return cartItems.reduce((acc, item) => acc + parseFloat(item.newPrice), 0).toFixed(2);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert("Please agree to the Terms of Service.");
      return;
    }
    
    try {
      setIsCheckingOut(true);
      const res = await fetch(`${API_URL}/api/create-invoice`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ billingInfo: formData }) // Passed to backend if needed in future
      });
      const data = await res.json();
      
      if (data.invoice_url && data.orderId) {
        // Redirect directly to NOWPayments!
        // When they hit back, they will safely land back on this Checkout page.
        window.location.href = data.invoice_url;
      } else {
        alert('Error creating invoice');
        setIsCheckingOut(false);
      }
    } catch (e) {
      console.error(e);
      alert('Error creating invoice');
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="pt-24 pb-20 px-6 max-w-7xl mx-auto min-h-screen">
      <div className="mb-12">
        <h1 className="text-[32px] md:text-[40px] font-bold text-white mb-2">Secure Checkout</h1>
        <p className="text-on-surface-variant text-[16px]">Fill in your details and select a payment method to complete your order.</p>
      </div>

      <form onSubmit={handleCheckout} className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        
        {/* Left Column: Billing Info */}
        <div className="bg-surface-container border border-white/5 rounded-3xl p-8 shadow-xl">
          <h2 className="text-[20px] font-bold text-white mb-8 border-b border-white/10 pb-4">1. Customer & Billing Info</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-on-surface-variant text-[14px] font-bold mb-2">Full Name</label>
              <input 
                type="text" 
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-on-surface-variant text-[14px] font-bold mb-2">Email Address</label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full bg-[#131418] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-on-surface-variant text-[14px] font-bold mb-2">Billing Address</label>
            <input 
              type="text" 
              name="billingAddress"
              value={formData.billingAddress}
              onChange={handleInputChange}
              required
              className="w-full bg-[#131418] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          <div className="mb-6">
            <label className="block text-on-surface-variant text-[14px] font-bold mb-2">Country</label>
            <select 
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full bg-[#131418] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary transition-colors appearance-none"
            >
              <option value="United States">United States</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Canada">Canada</option>
              <option value="Australia">Australia</option>
              <option value="Germany">Germany</option>
              <option value="France">France</option>
              <option value="India">India</option>
              {/* Add more as needed */}
            </select>
          </div>
        </div>

        {/* Right Column: Order Summary */}
        <div className="bg-surface-container border border-white/5 rounded-3xl p-8 shadow-xl h-fit sticky top-24">
          <h2 className="text-[20px] font-bold text-white mb-8 border-b border-white/10 pb-4">Order Details</h2>
          
          <div className="space-y-6 mb-8">
            {cartItems.map(item => (
              <div key={item.id} className="flex gap-4 items-center">
                <img src={item.image ? getValidImageUrl(item.image) : ''} alt={item.name} className="w-16 h-16 object-cover rounded bg-[#131418]" />
                <div className="flex-1">
                  <h4 className="font-bold text-white text-[14px]">{item.name}</h4>
                  <p className="text-on-surface-variant text-[12px] uppercase tracking-wider">{item.platform} • Qty: 1</p>
                </div>
                <div className="font-bold text-white">${item.newPrice}</div>
              </div>
            ))}
          </div>

          <div className="mb-8">
            <label className="block text-on-surface-variant text-[12px] font-bold uppercase tracking-wider mb-2">PROMO CODE</label>
            <div className="flex gap-2">
              <input 
                type="text" 
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="ENTER CODE" 
                className="flex-1 bg-[#131418] border border-white/10 rounded-full px-4 py-2 text-white focus:outline-none focus:border-primary text-[14px]"
              />
              <button type="button" className="bg-white/5 text-white px-6 py-2 rounded-full font-bold text-[14px] hover:bg-white/10 transition-colors">Apply</button>
            </div>
          </div>

          <div className="space-y-3 mb-6 border-t border-white/10 pt-6">
            <div className="flex justify-between text-on-surface-variant text-[14px]">
              <span>Subtotal</span>
              <span>${calculateTotal()}</span>
            </div>
            <div className="flex justify-between text-on-surface-variant text-[14px]">
              <span>Taxes</span>
              <span>$0.00</span>
            </div>
          </div>

          <div className="flex justify-between items-center mb-8 border-t border-white/10 pt-6">
            <span className="font-bold text-white text-[18px]">Total Due</span>
            <span className="font-bold text-primary text-[24px]">${calculateTotal()}</span>
          </div>

          <label className="flex items-start gap-3 mb-8 cursor-pointer">
            <input 
              type="checkbox" 
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 accent-primary" 
              required
            />
            <span className="text-on-surface-variant text-[12px] leading-relaxed">
              I agree to the <span className="text-primary hover:underline">Terms of Service</span> and understand that all digital purchases are delivered instantly and are subject to the Refund Policy.
            </span>
          </label>

          <button 
            type="submit"
            disabled={isCheckingOut || !agreedToTerms}
            className={`w-full font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-all ${isCheckingOut || !agreedToTerms ? 'bg-primary/50 text-[#131418]/50 cursor-not-allowed' : 'bg-primary text-[#131418] hover:shadow-[0_0_15px_rgba(240,185,11,0.5)]'}`}
          >
            {isCheckingOut ? (
              <div className="flex gap-2 h-6 items-center">
                <div className="candle !h-4 !w-1 !bg-[#131418]"></div>
                <div className="candle !h-4 !w-1 !bg-[#131418]"></div>
                <div className="candle !h-4 !w-1 !bg-[#131418]"></div>
              </div>
            ) : (
              <><span className="material-symbols-outlined text-[20px]">account_balance_wallet</span> Pay with Crypto</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
